/* eslint-disable @typescript-eslint/no-empty-object-type */
import { NgClass, NgFor, NgIf } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { NbButtonModule, NbCardModule, NbDialogService, NbIconModule, NbTooltipModule } from '@nebular/theme';
import { Actions, ofActionCompleted, ofActionDispatched, Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { OktaRestriction } from 'src/app/core/services/okta-restrictions.service';
import { UtilityService } from 'src/app/core/services/utility.service';
import { RouterState } from 'src/app/features/auth/redux';
import { ENVIRONMENTS } from 'src/app/shared/constants/general-constants';
import { NgxConfirmModalComponent } from 'src/app/shared/ui/ngx-confirm-modal/ngx-confirm-modal.component';
import { SpinnerState } from 'src/app/shared/ui/spinner/redux/spinner.state';
import { ENVIRONMENT } from 'src/environments/environment';
import { SharedModule } from '../../../shared/shared.module';
import {
  BuildDrb,
  BuildDrbFail,
  BuildDrbSuccess,
  DrbState,
  LoadDiff,
  LoadDiffFail,
  LoadDiffSuccess,
  NoDrbDiff
} from '../redux';
import { ChangedComponent } from './changed/changed.component';
import { SourceAddedComponent } from './source-added/source-added.component';
import { SourceRemovedComponent } from './source-removed/source-removed.component';
import { XliffAddedComponent } from './xliff-added/xliff-added.component';
import { XliffRemovedComponent } from './xliff-removed/xliff-removed.component';

@Component({
  selector: 'app-drb-diff',
  templateUrl: './drb-diff.component.html',
  styleUrls: ['./drb-diff.component.css'],
  imports: [
    NgIf,
    SharedModule,
    NbCardModule,
    NgClass,
    NbIconModule,
    NbButtonModule,
    NbTooltipModule,
    NgFor,
    XliffAddedComponent,
    ChangedComponent,
    XliffRemovedComponent,
    SourceAddedComponent,
    SourceRemovedComponent
  ]
})
export class DrbDiffComponent implements OnInit, OnDestroy {
  public username$: Observable<string> = inject(Store).select(RouterState.username);
  public diff$: Observable<any> = inject(Store).select(DrbState.diff);
  public deploying$: Observable<any> = inject(Store).select(DrbState.deploying);
  public translating$: Observable<any> = inject(Store).select(DrbState.translating);
  public loading$: Observable<boolean> = inject(Store).select(SpinnerState.isLoading);
  private unsubscribe = new Subject<void>();

  public spin;
  public diff = {
    diff: null,
    owner: null,
    draft_version: null,
    candidate_version: null,
    last_successful_version: null,
    draft_status: null,
    candidate_status: null
  };
  public diffXliff;
  public diffSources;
  public username;
  public allowDeploy: boolean;
  public deploying;
  public disableButton = false;
  public environment: string = ENVIRONMENT.name;
  public noDrb = true;
  public translating;
  public successStatus = ['BUILD_SUCCEEDED', 'DEPLOY_SUCCEEDED_PROD', 'DEPLOY_SUCCEEDED_STAGE'];
  public failStatus = ['BUILD_FAILED', 'DEPLOY_FAILED_PROD', 'DEPLOY_FAILED_STAGE'];
  public startStatus = ['BUILD_STARTED', 'DEPLOY_STARTED_PROD', 'DEPLOY_STARTED_STAGE'];
  public bundle_name = '';

  constructor(
    private store: Store,
    private router: Router,
    private action$: Actions,
    public utility: UtilityService,
    private dialogService: NbDialogService,
    private restrictionService: OktaRestriction
  ) {}

  public ngOnInit() {
    this.detectRouteChanges();
    this.bundle_name = this.router.url.split('/')[2];
    this.refresh();

    this.username$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.username = data));
    this.deploying$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.deploying = data));
    this.loading$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.spin = data));
    this.translating$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.translating = data));
    this.action$.pipe(ofActionCompleted(LoadDiffSuccess), takeUntil(this.unsubscribe)).subscribe((data) => {
      this.noDrb = false;
      this.diff = data.action.payload;
      if (data.action.payload.diff) {
        this.diffXliff = this.formatDiff(this.diff.diff.xliff.en);
        this.diffSources = [
          {
            sourceName: 'Files',
            diffItems: this.formatDiff(this.diff.diff.source.files)
          },
          {
            sourceName: 'Images',
            diffItems: this.formatDiff(this.diff.diff.source.images)
          },
          {
            sourceName: 'Links',
            diffItems: this.formatDiff(this.diff.diff.source.links)
          },
          {
            sourceName: 'Videos',
            diffItems: this.formatDiff(this.diff.diff.source.videos)
          }
        ];

        if (
          ENVIRONMENT.isProduction &&
          this.username === this.diff.owner &&
          this.restrictionService.drb_manager_permission
        ) {
          this.allowDeploy = true;
        }

        if (ENVIRONMENT.name === ENVIRONMENTS.STAGE && this.username === this.diff.owner) {
          this.allowDeploy = true;
        }
      }
    });
    this.action$.pipe(ofActionDispatched(BuildDrb), takeUntil(this.unsubscribe)).subscribe((data) => {
      if (data) {
        this.allowDeploy = false;
      }
    });
    this.action$
      .pipe(ofActionCompleted(BuildDrbFail, BuildDrbSuccess), takeUntil(this.unsubscribe))
      .subscribe((data) => {
        if (data) {
          this.allowDeploy = true;
        }
      });
    this.action$.pipe(ofActionCompleted(LoadDiffFail, NoDrbDiff), takeUntil(this.unsubscribe)).subscribe(() => {
      this.diff = {
        diff: null,
        owner: null,
        draft_version: null,
        candidate_version: null,
        last_successful_version: null,
        draft_status: null,
        candidate_status: null
      };
    });
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public deployNewDrb() {
    this.disableButton = true;
    this.dialogService
      .open(NgxConfirmModalComponent, {
        context: {
          title: $localize`Deploy DRB version ` + this.diff.draft_version,
          yesButton: $localize`Deploy`,
          noButton: $localize`Cancel`,
          message:
            $localize`Before deploying please confirm that you have verified all the changes and understand there will be no rollbacks.` +
            '\n\n' +
            $localize`To deploy, type CONFIRM below and click DEPLOY`
        }
      })
      .onClose.subscribe({
        next: (data) => {
          if (data) {
            this.store.dispatch(new BuildDrb(this.bundle_name));
          } else {
            this.disableButton = false;
          }
        }
      });
  }

  public refresh() {
    this.store.dispatch(new LoadDiff(this.bundle_name));
  }

  public trackByIndex(index: number): number {
    return index;
  }

  public formatDiff(diff) {
    return {
      added: this.utility.formatDictToObject(diff.added),
      updated: this.formatUpdatedDiff(diff.updated),
      removed: this.utility.formatDictToObject(diff.removed)
    };
  }

  public formatUpdatedDiff(data) {
    const formatedData = [];
    const files = Object.entries(data).map(([key, values]) => ({ id: key, ...(values as {}) }));
    files.forEach((element) => {
      const change = Object.entries(element).map(([key, values]) =>
        key !== 'id'
          ? {
              id: element.id,
              attribute: key,
              ...(values as {})
            }
          : null
      );
      change.forEach((el) => {
        if (el) {
          formatedData.push(el);
        }
      });
    });

    return formatedData;
  }

  public getStatusClass(): string {
    if (
      this.successStatus.includes(this.diff.draft_status) ||
      this.successStatus.includes(this.diff.candidate_status)
    ) {
      return 'added-background';
    } else if (
      this.failStatus.includes(this.diff.draft_status) ||
      this.failStatus.includes(this.diff.candidate_status)
    ) {
      return 'removed-background';
    } else if (
      this.startStatus.includes(this.diff.draft_status) ||
      this.startStatus.includes(this.diff.candidate_status)
    ) {
      return 'changed-background';
    } else {
      return '';
    }
  }

  public isDeployFailed(): boolean {
    return this.failStatus.includes(this.diff.draft_status) || this.failStatus.includes(this.diff.candidate_status);
  }

  public getDraftOrCandidateVersion(): string {
    if (this.environment === ENVIRONMENTS.STAGE) {
      return this.diff.draft_version;
    } else if (this.environment === ENVIRONMENTS.PROD) {
      return this.diff.candidate_version;
    } else {
      return '';
    }
  }

  public getDraftOrCandidateStatus(): string {
    if (this.environment === ENVIRONMENTS.STAGE) {
      return this.diff.draft_status;
    } else if (this.environment === ENVIRONMENTS.PROD) {
      return this.diff.candidate_status;
    } else {
      return '';
    }
  }

  public getLatestSuccessfulVersionLabel(): string {
    if (this.environment === ENVIRONMENTS.STAGE) {
      return $localize`Last Successful Stage Version: `;
    } else if (this.environment === ENVIRONMENTS.PROD) {
      return $localize`Last Successful Prod Version: `;
    } else {
      return '';
    }
  }

  public getDraftOrCandidateVersionLabel(): string {
    if (this.environment === ENVIRONMENTS.STAGE) {
      return $localize`Draft Version: `;
    } else if (this.environment === ENVIRONMENTS.PROD) {
      return $localize`Last Successful Stage Version: `;
    } else {
      return '';
    }
  }

  public getTitle(): string {
    if (this.environment === ENVIRONMENTS.STAGE) {
      return $localize`Validate, build and deploy`;
    } else if (this.environment === ENVIRONMENTS.PROD) {
      return $localize`Deploy`;
    } else {
      return '';
    }
  }

  public getSubtitle(): string {
    if (this.environment === ENVIRONMENTS.STAGE) {
      return $localize`Verify differences between the last successful DRB version and the draft version`;
    } else if (this.environment === ENVIRONMENTS.PROD) {
      return $localize`Verify differences between last successful Prod and Stage version`;
    } else {
      return '';
    }
  }

  public getNoDiffMessage(): string {
    const textSnip = this.environment === ENVIRONMENTS.PROD ? $localize`Prod and Stage` : $localize`Stage and Draft`;

    return $localize`There are no differences between last successful ${textSnip} version.`;
  }

  private detectRouteChanges(): void {
    this.router.events.pipe(takeUntil(this.unsubscribe)).subscribe((event) => {
      if (
        this.router.url.split('/')[1] === 'drb' &&
        this.bundle_name !== this.router.url.split('/')[2] &&
        event instanceof NavigationEnd
      ) {
        this.bundle_name = this.router.url.split('/')[2];
        this.refresh();
      }
    });
  }
}
